import React, { useEffect, useRef, useState, useMemo } from "react";
import DataGridSidebar from "./DataGridSidebar";
import {
  CURRENT_SELECTED_DEPARTMENT,
  generateColumnsFromData,
  generateRowsWithIds,
} from "../../utils/constants";

// Default Transaction type mapping and color configuration
const TRANSACTION_TYPES = {
  S: {
    label: "Sales",
    color: "#3b82f6",
    qtyColor: "#dc2626",
    isInbound: false,
  },
  R: {
    label: "Purchase",
    color: "#3b82f6",
    qtyColor: "#16a34a",
    isInbound: true,
  },
  C: {
    label: "Sales Return",
    color: "#f59e0b",
    qtyColor: "#16a34a",
    isInbound: true,
  },
  D: {
    label: "Purchase Return",
    color: "#8b5cf6",
    qtyColor: "#dc2626",
    isInbound: false,
  },
  I: {
    label: "Stock Transfer In",
    color: "#06b6d4",
    qtyColor: "#16a34a",
    isInbound: true,
  },
  O: {
    label: "Stock Transfer Out",
    color: "#ec4899",
    qtyColor: "#dc2626",
    isInbound: false,
  },
  L: {
    label: "Location Transfer",
    color: "#6366f1",
    qtyColor: "#dc2626",
    isInbound: false,
  },
  P: {
    label: "Project Issue",
    color: "#ef4444",
    qtyColor: "#dc2626",
    isInbound: false,
  },
  Op: {
    label: "Opening",
    color: "#64748b",
    qtyColor: "#64748b",
    isInbound: null,
  },
};

// Conditional formatting overrides based on the batch value
const BATCH_TRANSACTION_OVERRIDES = {
  Production: {
    I: { label: "Production In", color: "#10b981", qtyColor: "#16a34a" }, // Emerald Green
    O: { label: "Production Out", color: "#f43f5e", qtyColor: "#dc2626" },
    R: { label: "Production", color: "#10b981", qtyColor: "#16a34a" },
  },
  BT: {
    I: { label: "Batch Transfer In", color: "#0284c7", qtyColor: "#16a34a" }, // Sky Blue
    O: { label: "Batch Transfer Out", color: "#db2777", qtyColor: "#dc2626" }, // Dark Pink
  },
  DEMOI: {
    I: { label: "Demo Issue In", color: "#0d9488", qtyColor: "#16a34a" }, // Teal In
    O: { label: "Demo Issue Out", color: "#ea580c", qtyColor: "#dc2626" }, // Orange Out
  },
  DEMOR: {
    I: { label: "Demo Return In", color: "#16a34a", qtyColor: "#16a34a" }, // Green In
    O: { label: "Demo Return Out", color: "#e11d48", qtyColor: "#dc2626" }, // Red Out
  },
};

const AllDoSidebar = ({ selectedProduct, mode, exportFileName }) => {
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(
    CURRENT_SELECTED_DEPARTMENT,
  );
  const [departmentList, setDepartmentList] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trYear, setTrYear] = useState("-");
  const isResettingTrYear = useRef(false);

  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";
  const API_PATHS = JSON.parse(localStorage.getItem("apiPaths")) || "-";
  const API_BASE_URL = `${API_PATHS[0].API_CONFIG}/api/CRMItemTransHistory/${COMPANY_CODE}`;

  // Dynamically extract transaction labels and styles based on row context
  const getTypeInfo = (typeCode, batchVal) => {
    const cleanBatch = String(batchVal || "").trim();

    // Evaluate if a custom override matches this specific batch and transaction type
    if (
      BATCH_TRANSACTION_OVERRIDES[cleanBatch] &&
      BATCH_TRANSACTION_OVERRIDES[cleanBatch][typeCode]
    ) {
      return BATCH_TRANSACTION_OVERRIDES[cleanBatch][typeCode];
    }

    // Fall back to the default structural maps
    return (
      TRANSACTION_TYPES[typeCode] || {
        label: typeCode,
        color: "#64748b",
        qtyColor: "#64748b",
      }
    );
  };

  const configureColumns = (generatedColumns, isAllTransactions = false) => {
    return generatedColumns
      .filter((col) => col.field !== "Actqty")
      .map((col) => {
        // Configure Type column
        if (col.field === "Type") {
          return {
            ...col,
            headerName: "Transaction Type",
            width: 500,
            valueFormatter: (params) => {
              const typeCode = params.value;
              const batchVal = params.row?.batch;
              return getTypeInfo(typeCode, batchVal).label;
            },
            renderCell: (params) => {
              const typeCode = params.value;
              const batchVal = params.row?.batch;
              const typeInfo = getTypeInfo(typeCode, batchVal);

              return (
                <div
                  style={{
                    backgroundColor: typeInfo.color,
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  {typeInfo.label}
                </div>
              );
            },
          };
        }

        // Configure Qty column
        if (col.field === "Qty") {
          return {
            ...col,
            width: 100,
            renderCell: (params) => {
              const typeCode = params.row.Type;
              const batchVal = params.row?.batch;
              const typeInfo = getTypeInfo(typeCode, batchVal);
              const value = params.value;

              return (
                <div
                  style={{
                    color: typeInfo.qtyColor,
                    fontWeight: "700",
                    textAlign: "right",
                    width: "100%",
                    fontSize: "0.9rem",
                  }}
                >
                  {value != null ? Number(value).toFixed(3) : "0.000"}
                </div>
              );
            },
          };
        }

        return col;
      });
  };

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    let userAccessString = "";
    if (currentUser && currentUser[0] && currentUser[0].useraccess) {
      userAccessString = currentUser[0].useraccess;
    }
    let allDepartments = currentUser || [];
    const currentSelectedDepartment =
      localStorage.getItem("currentSelectedDepartment") || "-";

    if (userAccessString.includes("-CD")) {
      setDepartmentList([{ DEPTNO: currentSelectedDepartment }]);
      setSelectedDepartment(currentSelectedDepartment);
      return;
    }
    if (userAccessString.includes("-RP")) {
      setDepartmentList(allDepartments);
      if (!selectedDepartment) {
        setSelectedDepartment(currentSelectedDepartment);
      }
      return;
    }
    const allOption = { DEPTNO: "-" };
    setDepartmentList([allOption, ...allDepartments]);
    if (!selectedDepartment) {
      setSelectedDepartment("-");
    }
  }, []);

  useEffect(() => {
    if (trYear !== "-") {
      isResettingTrYear.current = true;
      setTrYear("-");
    }
  }, [mode]);

  useEffect(() => {
    if (!selectedProduct?.id || !mode) return;
    if (isResettingTrYear.current) {
      isResettingTrYear.current = false;
      return;
    }

    const safeProductId = String(selectedProduct?.Code || "").replace(
      /\//g,
      "%2F",
    );

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/${mode}/${safeProductId}/${selectedDepartment}/${trYear}/-`,
        );
        const data = await response.json();
        const isAllTransactions = mode === "All_Transactions";

        if (isAllTransactions && data && data.length > 0) {
          let runningTotal = 0;

          const dataWithRunningTotal = data.map((item, index) => {
            const actQty = Number(item.Actqty) || 0;
            if (index === 0) {
              runningTotal = actQty;
            } else {
              runningTotal += actQty;
            }

            return {
              ...item,
              RunningQty: parseFloat(runningTotal.toFixed(3)),
            };
          });

          const rowsWithIds = generateRowsWithIds(dataWithRunningTotal);
          setRows(rowsWithIds);

          const generatedColumns =
            generateColumnsFromData(dataWithRunningTotal);
          const configuredColumns = configureColumns(
            generatedColumns,
            isAllTransactions,
          );
          const filteredColumns = configuredColumns.filter(
            (col) => col.field !== "RunningQty",
          );
          const qtyIndex = filteredColumns.findIndex(
            (col) => col.field === "Qty",
          );

          const runningQtyColumn = {
            field: "RunningQty",
            headerName: "Running Qty",
            width: 120,
            type: "string",
            editable: false,
            sortable: true,
            filterable: true,
            hideable: true,
            valueFormatter: (params) => {
              const value = params.value;
              return value != null && !isNaN(value)
                ? Number(value).toFixed(3)
                : "0.000";
            },
            renderCell: (params) => {
              const value = params.value;
              const displayValue =
                value != null && !isNaN(value) ? String(value) : "0.000";
              return (
                <div style={{ textAlign: "right", width: "100%" }}>
                  {displayValue}
                </div>
              );
            },
          };

          let finalColumns = [...filteredColumns];
          if (qtyIndex !== -1) {
            finalColumns.splice(qtyIndex + 1, 0, runningQtyColumn);
          } else {
            finalColumns.push(runningQtyColumn);
          }

          setColumns(finalColumns);
        } else {
          const rowsWithIds = generateRowsWithIds(data);
          setRows(rowsWithIds);
          const generatedColumns = generateColumnsFromData(data);
          const configuredColumns = configureColumns(generatedColumns, false);
          setColumns(configuredColumns);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setRows([]);
        setColumns([]);
      }
      setLoading(false);
    };

    fetchData();
  }, [selectedProduct, mode, trYear, selectedDepartment]);

  const toggleTrYear = () => {
    setTrYear((prev) => (prev === "Prior" ? "-" : "Prior"));
  };

  const handleToggle = () => {
    setIsToggleOn(!isToggleOn);
  };

  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
  };

  const memoizedColumns = useMemo(() => columns, [columns]);
  const memoizedRows = useMemo(() => rows, [rows]);

  return (
    <DataGridSidebar
      selectedProduct={selectedProduct}
      columns={memoizedColumns}
      rows={memoizedRows}
      exportFileName={exportFileName}
      handleDepartmentChange={handleDepartmentChange}
      onToggleChange={handleToggle}
      departmentList={departmentList}
      selectedDepartment={selectedDepartment}
      pageSize={100}
      pageSizeOptions={[10, 25, 50, 100]}
      height={500}
      onToggleTrYear={toggleTrYear}
      currentTrYear={trYear}
      title={exportFileName}
    />
  );
};

export default AllDoSidebar;
